import { useState, type FC } from "react";
import { useSelector } from "react-redux";

import { Button } from "@/components/ui/button";
import { ListCard, ListRow } from "@/components/ui/list-card";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

import type { ClassicOptions } from "../../../api/types/classicDrafting";
import type { LawyerReducer } from "../../../redux/lawyer/lawyerSlice";
import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import {
  BUILTIN_TEMPLATE,
  NO_LETTERHEAD,
  letterheadGroups,
  notReadyLabel,
  researchMeta,
  researchSummary,
  templateValue,
  type ClassicPick
} from "./classicModel";

type Engine = "casey" | "classic";

const NOT_AVAILABLE = "No classic template for this draft type";

type Props = {
  /** From the draft list: whether Classic covers this draft. */
  classicAvailable: boolean;
  /** Null while loading, or when Classic isn't available. */
  options: ClassicOptions | null;
  optionsError: string | null;
  pick: ClassicPick | null;
  onPickChange: (pick: ClassicPick) => void;
  onContinue: () => void;
  onStartInChat: () => void;
};

type RowsProps = {
  options: ClassicOptions | null;
  optionsError: string | null;
  pick: ClassicPick | null;
  onPickChange: (pick: ClassicPick) => void;
  firmName: string;
};

const ClassicRows: FC<RowsProps> = ({
  options,
  optionsError,
  pick,
  onPickChange,
  firmName
}) => {
  if (optionsError) {
    return (
      <ListRow
        title="Classic Options"
        description={optionsError}
        control={<span className="text-sm text-fg-4">Unavailable</span>}
      />
    );
  }
  if (!options || !pick) {
    return (
      <>
        {[0, 1, 2].map((i) => (
          <ListRow
            key={i}
            title={<Skeleton className="h-3.5 w-24" />}
            description={<Skeleton className="mt-1 h-3 w-64" />}
          />
        ))}
      </>
    );
  }

  const builtin = options.templates.filter((t) => t.source === "caseblink");
  const firm = options.templates.filter((t) => t.source === "firm");
  const current = options.templates.find((t) => t.id === pick.templateId);
  const { yours, firm: firmLetterheads } = letterheadGroups(
    options.letterheads
  );
  const { strategy } = options;

  const chooseTemplate = (value: string) => {
    const id = value === BUILTIN_TEMPLATE ? null : value;
    const t = options.templates.find((x) => x.id === id);
    onPickChange({
      ...pick,
      templateId: id,
      templateName: t?.name ?? "CaseBlink"
    });
  };
  const chooseLetterhead = (value: string) => {
    const l = options.letterheads.find((x) => x.id === value);
    onPickChange({
      ...pick,
      letterheadId: l?.id ?? null,
      letterheadName: l?.name ?? null
    });
  };

  return (
    <>
      <ListRow
        title="Template"
        description={
          current?.source === "firm"
            ? "From your firm's old Templates page, as reviewed there."
            : "CaseBlink's built-in classic template."
        }
        control={
          <Select
            value={pick.templateId ?? BUILTIN_TEMPLATE}
            onValueChange={chooseTemplate}
          >
            <SelectTrigger className="w-60" aria-label="Template">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="w-95">
              {builtin.length ? (
                <SelectGroup>
                  <SelectLabel>CaseBlink</SelectLabel>
                  {builtin.map((t) => (
                    <SelectItem key={templateValue(t)} value={templateValue(t)}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ) : null}
              {builtin.length && firm.length ? <SelectSeparator /> : null}
              {firm.length ? (
                <SelectGroup>
                  <SelectLabel>{firmName || "Your Firm"}</SelectLabel>
                  {firm.map((t) => (
                    <SelectItem
                      key={templateValue(t)}
                      value={templateValue(t)}
                      disabled={!t.ready}
                    >
                      <span className="truncate">{t.name}</span>
                      {t.ready ? null : (
                        <StatusBadge tone="warn">
                          {notReadyLabel(t.reason)}
                        </StatusBadge>
                      )}
                    </SelectItem>
                  ))}
                  <div className="px-2 py-1.5 text-sm text-fg-3">
                    As reviewed on the old Templates page. Later edits in Firm
                    Knowledge aren't used.
                  </div>
                </SelectGroup>
              ) : null}
            </SelectContent>
          </Select>
        }
      />
      {options.letterheads.length ? (
        <ListRow
          title="Letterhead"
          description="Printed on the first page."
          control={
            <Select
              value={pick.letterheadId ?? NO_LETTERHEAD}
              onValueChange={chooseLetterhead}
            >
              <SelectTrigger className="w-60" aria-label="Letterhead">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="w-75">
                <SelectItem value={NO_LETTERHEAD}>No Letterhead</SelectItem>
                {yours.length ? (
                  <SelectGroup>
                    <SelectLabel>Yours</SelectLabel>
                    {yours.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ) : null}
                {firmLetterheads.length ? (
                  <SelectGroup>
                    <SelectLabel>Firm</SelectLabel>
                    {firmLetterheads.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        <span className="truncate">{l.name}</span>
                        {l.uploaded_by ? (
                          <span className="text-sm text-fg-4">
                            {l.uploaded_by}
                          </span>
                        ) : null}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ) : null}
              </SelectContent>
            </Select>
          }
        />
      ) : null}
      <ListRow
        title="Draft Strategy"
        description={
          strategy && strategy.total
            ? `${strategy.answered} of ${strategy.total} answered. You review it next, before anything is written.`
            : "You review it next, before anything is written."
        }
        control={<span className="text-sm text-fg-3">Next Step</span>}
      />
      <ListRow
        title="Research"
        description={researchSummary(options.research)}
        control={
          options.research.length ? (
            <span className="text-sm text-fg-3">
              {researchMeta(options.research)}
            </span>
          ) : null
        }
      />
    </>
  );
};

/**
 * Where the editor would be for a draft nothing has written yet, at firms on
 * the Classic allowlist: write it with Casey in chat, or with the old
 * one-pass template writer. Opens in Classic whenever Classic covers the
 * draft; nothing is saved until Continue.
 */
export const ClassicChooser: FC<Props> = ({
  classicAvailable,
  options,
  optionsError,
  pick,
  onPickChange,
  onContinue,
  onStartInChat
}) => {
  const [engine, setEngine] = useState<Engine>(
    classicAvailable ? "classic" : "casey"
  );
  const firmName = useSelector(
    (s: LawyerReducer) => lawyerSelectors.selectAll(s).lawFirm
  );
  const classic = engine === "classic";

  return (
    <div
      className="flex h-full min-h-0 justify-center overflow-auto px-6 pt-10 pb-6"
      data-testid="classic-chooser"
    >
      <div className="flex w-full max-w-155 flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="flex flex-1 flex-col gap-0.5">
            <span className="text-base font-medium text-fg">
              Write This Draft
            </span>
            <span className="text-sm text-fg-3">
              You can revise either kind with Casey afterward.
            </span>
          </div>
          <Tabs value={engine} onValueChange={(v) => setEngine(v as Engine)}>
            <TabsList variant="segmented">
              <TabsTrigger value="casey">Casey</TabsTrigger>
              {classicAvailable ? (
                <TabsTrigger value="classic">Classic</TabsTrigger>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <TabsTrigger value="classic" disabled>
                        Classic
                      </TabsTrigger>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{NOT_AVAILABLE}</TooltipContent>
                </Tooltip>
              )}
            </TabsList>
          </Tabs>
        </div>

        <ListCard>
          {classic ? (
            <ClassicRows
              options={options}
              optionsError={optionsError}
              pick={pick}
              onPickChange={onPickChange}
              firmName={firmName}
            />
          ) : (
            <>
              <ListRow
                title="Firm Style"
                description="Casey follows your firm's Automations for this kind of draft."
                control={
                  <span className="text-sm text-fg-3">Firm Knowledge</span>
                }
              />
              <ListRow
                title="Where It Happens"
                description="Casey drafts in the chat panel and asks before anything destructive."
                control={<span className="text-sm text-fg-3">Chat</span>}
              />
              {classicAvailable ? null : (
                <ListRow
                  title="Classic Draft"
                  description="There's no classic template for this draft type."
                  control={
                    <span className="text-sm text-fg-4">Not Available</span>
                  }
                />
              )}
            </>
          )}
        </ListCard>

        <div className="flex justify-end">
          {classic ? (
            <Button
              variant="primary"
              disabled={!options || !pick}
              onClick={onContinue}
              data-testid="classic-continue"
            >
              Continue
            </Button>
          ) : (
            <Button variant="primary" onClick={onStartInChat}>
              Start in Chat
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
